import {
  CleanDeliveryOrder,
  RbacObject,
} from '../../../export-types/cleaned-types';
import { PurchaseOrderSubRow } from './delivery-orders-tab-item';
import React, { useCallback } from 'react';
import classNames from 'classnames';
import { useRbac } from '../../common/Rbac';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { TariffCell } from '../../common/cards/tariff-cell-card.component';
import moment from 'moment/moment';
import { FormattedMessage, useIntl } from 'react-intl';
import { Manager } from '../../common/cards/manager-cell-card.component';
import { checkDeliveryReadyToSent, toMoney } from '../../../utils/common.utils';
import { useProfile } from '../../../api/profile';
import { ExpandButton, Row } from '../../row';
import { OutletContextParams } from './delivery-orders-screen';
import { ModalsService } from '../../../services/modals.service';
import { DeliveryOrderPackageModal } from './delivery-order-package-modal';

type Props = {
  data: CleanDeliveryOrder;
  collapsed?: boolean;
  onCollapsedChange?: () => void;
  onUpdate: () => void;
};

export function PackagingTabItem(props: Props) {
  const { data: profile } = useProfile();
  const intl = useIntl();

  const { hasPermission } = useRbac();
  const navigate = useNavigate();
  const {
    selectedOrders,
    onChangeSelectedOrders,
    selectAllOrders,
    setSelectAllOrders,
  } = useOutletContext<OutletContextParams>();

  const canReadExtendedData = hasPermission(
    RbacObject.DeliveryOrder,
    'read:finances-client',
  );

  const href = `/deliveries/delivery-order/${props.data.id}`;

  const handleClick = useCallback(() => {
    navigate(href);
  }, [navigate, href]);

  const onToggle = () => {
    const id = props.data.internalId;
    const existedOrderIndex = selectedOrders.findIndex(
      (order) => order.internalId === id,
    );
    if (existedOrderIndex !== -1) {
      const newOrders = [...selectedOrders];
      newOrders.splice(existedOrderIndex, 1);
      onChangeSelectedOrders(newOrders);
    } else {
      onChangeSelectedOrders([...selectedOrders, props.data]);
    }
  };

  return (
    <Row
      onClick={handleClick}
      collapsed={props.collapsed}
      onCollapsedChange={props.onCollapsedChange}
      defaultCollapsed={true}
      subRows={props.data.purchaseOrders?.map((purchaseOrder) => (
        <PurchaseOrderSubRow
          key={purchaseOrder.id}
          data={purchaseOrder}
          status={'packaged'}
        />
      ))}
    >
      {({ collapsed, setCollapsed }) => (
        <>
          {canReadExtendedData && (
            <div className="col-1 text-truncate">
              {props.data.supplier?.name ?? ''}
            </div>
          )}
          <div className="col-1">
            <Link
              to={href}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              D{props.data.internalId}
            </Link>
          </div>
          <div className="col-1">
            <ExpandButton
              onToggle={() => setCollapsed(!collapsed)}
              collapsed={collapsed}
            />
          </div>
          <div className="col-1 d-flex">
            <TariffCell deliveryMethod={props.data.deliveryMethod!} />
          </div>
          <div className={'col-1 full-text'}>
            <input
              className={classNames(
                'form-check-input p-2 border-2',
                checkDeliveryReadyToSent(props.data)
                  ? 'border-green-400 cursor-pointer'
                  : 'border-gray-400',
              )}
              type="checkbox"
              disabled={!checkDeliveryReadyToSent(props.data)}
              checked={
                checkDeliveryReadyToSent(props.data) &&
                (selectedOrders.findIndex(
                  (order) => order.internalId === props.data.internalId,
                ) !== -1 ||
                  selectAllOrders)
              }
              onClick={(e) => {
                e.stopPropagation();
                if (
                  selectedOrders.findIndex(
                    (order) => order.internalId === props.data.internalId,
                  ) !== -1
                ) {
                  setSelectAllOrders(false);
                }
                onToggle();
              }}
            />
          </div>
          <div className="col-1 full-text">
            <div className="color-gray-800 fs-16 fw-500">
              {moment(props.data.packagedDate).format('DD.MM.YYYY')}
            </div>
            <div className="color-gray-600 fs-14">
              <FormattedMessage
                defaultMessage="Упаковано"
                id="delivery.status-label.packed"
                description="Статус доставки"
              />
            </div>
          </div>
          <div className="col-1 text-truncate">
            <div className={'fw-500'}>
              {intl.formatMessage({
                id: 'deliveryOrder.storagePeriod',
                defaultMessage: 'Срок хранения',
                description: 'Надпись на вкладке',
              })}
            </div>
            {(() => {
              const now = moment();
              const packaged = moment(props.data.packagedDate);
              const duration = moment.duration(now.diff(packaged));
              const days = Math.floor(duration.asDays());
              const hours = duration.hours();

              const isWarning = days > 4;
              const className = isWarning ? 'color-orange' : '';

              return (
                <div className={className}>{`${days} ${intl.formatMessage({
                  id: 'deliveryOrder.day',
                  defaultMessage: 'дн.',
                  description: 'Надпись на вкладке',
                })} ${hours} ${intl.formatMessage({
                  id: 'deliveryOrder.hour',
                  defaultMessage: 'ч.',
                  description: 'Надпись на вкладке',
                })}`}</div>
              );
            })()}
          </div>
          <div className={'col-1'}>
            <div
              className={'color-orange cursor-pointer hover-bg-light'}
              onClick={(e) => {
                e.stopPropagation();
                ModalsService.createModal(DeliveryOrderPackageModal, {
                  deliveryOrderId: props.data.id,
                  defaultValues: props.data,
                  showAdditional: true,
                }).then(() => {
                  props.onUpdate();
                });
              }}
            >
              {checkDeliveryReadyToSent(props.data)
                ? `${props.data.count}; ${props.data.volume} ${intl.formatMessage(
                    {
                      id: 'deliveryOrder.volume',
                      defaultMessage: 'м³',
                      description: 'Надпись на вкладке',
                    },
                  )}; ${props.data.weight} ${intl.formatMessage({
                    id: 'deliveryOrder.kg',
                    defaultMessage: 'кг',
                    description: 'Надпись на вкладке',
                  })}`
                : intl.formatMessage({
                    id: 'deliveryOrders.no-data',
                    defaultMessage: 'Нет данных',
                    description: 'Надпись на вкладке',
                  })}
            </div>
          </div>
          {profile?.role === 'admin' ||
          ((profile?.accesses.includes('purchase') ||
            profile?.accesses.includes('warehouse')) &&
            !!profile.supplier) ? (
            <div
              className={classNames(
                'col-1',
                canReadExtendedData ? '' : 'text-end',
                props.data?.deliveryMethod?.destination?.city !== 'Москва'
                  ? 'fw-medium'
                  : null,
              )}
            >
              {props.data?.deliveryMethod?.destination?.city}
            </div>
          ) : null}
          {canReadExtendedData && props.data.user?.manager ? (
            <div className="col  text-truncate">
              <Manager manager={props.data.user?.manager} />
            </div>
          ) : (
            <div className="col" />
          )}
          {profile && !profile.accesses.includes('fulfillment') && (
            <div className="col text-end">
              <div className="color-gray-800 fs-16 fw-500  text-truncate">
                {toMoney(
                  props.data.moderateProductPrice!,
                  props.data.supplier?.purchaseCurrency || 'cny',
                )}
              </div>
              <div className="color-gray-600 fs-14 fw-400">
                <FormattedMessage
                  defaultMessage="Стоимость товаров"
                  id="delivery.label.priceOfGoods"
                  description="Стоимость товаров"
                />
              </div>
            </div>
          )}
        </>
      )}
    </Row>
  );
}
